import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const NAV_ITEMS = [
  {
    path: '/',
    label: 'Inicio',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
        <polyline points="9 22 9 12 15 12 15 22"/>
      </svg>
    ),
  },
  {
    path: '/planeacion',
    label: 'Planeación diaria',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
        <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/>
        <line x1="3" y1="10" x2="21" y2="10"/>
      </svg>
    ),
  },
];

export default function Layout() {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);

  const handleLogout = () => { logout(); navigate('/login'); };

  return (
    <div style={styles.shell}>
      {/* ── Sidebar ── */}
      <aside style={{ ...styles.sidebar, width: collapsed ? 56 : 220 }}>
        {/* Brand */}
        <div style={styles.brand}>
          <div className="logo-circle">VS</div>
          {!collapsed && <span style={styles.brandName}>VivaSmart</span>}
        </div>

        {/* Nav */}
        <nav style={styles.nav}>
          {NAV_ITEMS.map(item => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/'}
              title={collapsed ? item.label : undefined}
              style={({ isActive }) => ({
                ...styles.navItem,
                ...(isActive ? styles.navItemActive : {}),
                justifyContent: collapsed ? 'center' : 'flex-start',
                padding: collapsed ? '10px' : '9px 14px',
              })}
            >
              <span style={{ flexShrink: 0, opacity: 0.9 }}>{item.icon}</span>
              {!collapsed && <span style={{ fontSize: 13, fontWeight: 500 }}>{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* Bottom */}
        <div style={styles.sidebarBottom}>
          <button
            onClick={() => setCollapsed(!collapsed)}
            title={collapsed ? 'Expandir menú' : 'Colapsar menú'}
            style={styles.collapseBtn}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              {collapsed
                ? <><polyline points="9 18 15 12 9 6"/><polyline points="3 18 9 12 3 6"/></>
                : <><polyline points="15 18 9 12 15 6"/><polyline points="21 18 15 12 21 6"/></>
              }
            </svg>
          </button>
        </div>
      </aside>

      {/* ── Right panel ── */}
      <div style={styles.rightPanel}>
        {/* Topbar */}
        <header style={styles.topbar}>
          <div style={styles.topbarLeft}>
            {/* breadcrumb placeholder — se llenaría por ruta */}
          </div>
          <div style={styles.topbarRight}>
            <div style={styles.userChip}>
              <div style={styles.userAvatar}>
                {(user?.email || 'U')[0].toUpperCase()}
              </div>
              {user?.email && (
                <div style={styles.userInfo}>
                  <span style={styles.userName}>{user.email.split('@')[0]}</span>
                  <span style={styles.userRole}>Usuario</span>
                </div>
              )}
            </div>
            <button
              onClick={handleLogout}
              title="Cerrar sesión"
              style={styles.logoutBtn}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                <polyline points="16 17 21 12 16 7"/>
                <line x1="21" y1="12" x2="9" y2="12"/>
              </svg>
            </button>
          </div>
        </header>

        {/* Main content */}
        <main style={styles.main}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}

const styles = {
  shell: {
    display: 'flex',
    height: '100vh',
    overflow: 'hidden',
  },
  /* Sidebar */
  sidebar: {
    background: 'var(--sidebar-bg)',
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    transition: 'width 0.2s ease',
    overflow: 'hidden',
    position: 'relative',
    zIndex: 10,
  },
  brand: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '14px 12px',
    borderBottom: '1px solid var(--sidebar-border)',
    minHeight: 56,
    overflow: 'hidden',
  },
  brandName: {
    fontSize: 15,
    fontWeight: 700,
    color: '#fff',
    whiteSpace: 'nowrap',
    letterSpacing: '-0.01em',
  },
  nav: {
    display: 'flex',
    flexDirection: 'column',
    padding: '8px 6px',
    gap: 2,
    flex: 1,
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    borderRadius: 'var(--radius)',
    color: 'var(--sidebar-text)',
    textDecoration: 'none',
    transition: 'var(--transition)',
    whiteSpace: 'nowrap',
  },
  navItemActive: {
    background: 'var(--sidebar-active)',
    color: 'var(--sidebar-text-act)',
  },
  sidebarBottom: {
    padding: '8px 6px 12px',
    borderTop: '1px solid var(--sidebar-border)',
    display: 'flex',
    justifyContent: 'center',
  },
  collapseBtn: {
    background: 'transparent',
    border: 'none',
    color: 'var(--sidebar-text)',
    cursor: 'pointer',
    padding: '6px',
    borderRadius: 'var(--radius)',
    display: 'flex',
    alignItems: 'center',
    transition: 'var(--transition)',
  },
  /* Right panel */
  rightPanel: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    background: 'var(--bg-primary)',
  },
  topbar: {
    height: 56,
    background: 'var(--topbar-bg)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 24px',
    flexShrink: 0,
    boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
  },
  topbarLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    color: 'rgba(255,255,255,0.7)',
    fontSize: 13,
  },
  topbarRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  userChip: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  userAvatar: {
    width: 32, height: 32,
    background: 'rgba(255,255,255,0.2)',
    borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 13, fontWeight: 700, color: '#fff',
    flexShrink: 0,
  },
  userInfo: {
    display: 'flex',
    flexDirection: 'column',
  },
  userName: {
    fontSize: 13,
    fontWeight: 600,
    color: '#fff',
    lineHeight: 1.2,
  },
  userRole: {
    fontSize: 11,
    color: 'rgba(255,255,255,0.6)',
    lineHeight: 1.2,
  },
  logoutBtn: {
    background: 'rgba(255,255,255,0.1)',
    border: '1px solid rgba(255,255,255,0.15)',
    color: 'rgba(255,255,255,0.8)',
    borderRadius: 'var(--radius)',
    cursor: 'pointer',
    padding: '5px 8px',
    display: 'flex',
    alignItems: 'center',
    transition: 'var(--transition)',
  },
  main: {
    flex: 1,
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'column',
  },
};
